import React, { useRef, Children, lazy } from 'react';
import { motion, useInView } from 'framer-motion';
interface JourneyStep {
  image: string;
  title: string;
  description: string;
}
const steps: JourneyStep[] = [{
  image: 'https://www.solidaridadnetwork.org/wp-content/uploads/2022/08/practice-for-change-coffee-uganda.jpg',
  title: 'Farm Origin',
  description: 'GPS-tagged farms across Mt. Elgon and Rwenzori with soil data, altitude, variety, and harvest date, verified at the source.'
}, {
  image: 'https://ugandacoffee.go.ug/sites/default/files/2023-10/DSC_0871.JPG',
  title: 'Processing',
  description: 'Wet/dry processing tracked at Ugandan washing stations with water usage, energy consumption, and waste management data.'
}, {
  image: 'https://live.staticflickr.com/5013/5405349188_c08e656679_b.jpg',
  title: 'Export & Transit',
  description: 'Shipping routes from Mombasa port, carbon emissions per kg, customs clearance, and cold chain monitoring.'
}, {
  image: 'https://yorkemporium.co.uk/cdn/shop/articles/coffee-8406187_640_389d4dd3-dc9e-4bc9-b8fa-9eefd8b30f43.jpg?v=1756973831',
  title: 'Roasting',
  description: 'Roast profiles, energy usage, quality grading (Drugar, Bugisu AA), and batch-level carbon footprint calculations.'
}, {
  image: 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=400&h=400&fit=crop&crop=center',
  title: 'Retail & Consumer',
  description: 'QR-scannable packaging lets consumers trace their cup back to the Ugandan farmer who grew it.'
}];
export function CoffeeJourney() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section ref={ref} className="w-full bg-white py-20 sm:py-28" data-id="element-128">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-129">
        <motion.div className="text-center mb-16" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-130">

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-131">
            From farm to cup,{' '}
            <span className="text-carbon-bright" data-id="element-132">every step verified</span>
          </h2>
          <p className="mt-4 text-lg text-carbon-mid max-w-2xl mx-auto" data-id="element-133">
            CarbonSafe tracks every stage of the coffee supply chain, creating
            an immutable record of origin, quality, and environmental impact.
          </p>
        </motion.div>

        {/* Journey timeline */}
        <div className="relative" data-id="element-134">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-[72px] left-0 right-0 h-0.5 bg-gradient-to-r from-carbon-bright/20 via-carbon-bright/40 to-carbon-bright/20" aria-hidden="true" data-id="element-135" />


          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-4" initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: 0.12
            }
          }
        }} data-id="element-136">

            {steps.map((step, i) => <motion.div key={step.title} className="relative flex flex-col items-center text-center" variants={{
            hidden: {
              opacity: 0,
              y: 30
            },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                ease: [0.22, 1, 0.36, 1]
              }
            }
          }} data-id="element-137">

                {/* Image circle */}
                <div className="relative z-10 w-[88px] h-[88px] rounded-full overflow-hidden border-[3px] border-carbon-bright/30 shadow-lg shadow-carbon-bright/10 mb-5 bg-white" data-id="element-138">
                  <img src={step.image} alt={step.title} className="w-full h-full object-cover" loading="lazy" data-id="element-139" />

                </div>

                {/* Step number badge */}
                <div className="absolute top-0 right-1/2 translate-x-[38px] -translate-y-1 w-7 h-7 rounded-full bg-carbon-bright text-white text-xs font-bold flex items-center justify-center z-20 shadow-md" data-id="element-140">
                  {i + 1}
                </div>

                {/* Arrow between steps (desktop only) */}
                {i < steps.length - 1 && <div className="hidden lg:block absolute top-[72px] left-[calc(50%+50px)] w-[calc(100%-100px)] h-0.5" aria-hidden="true" data-id="element-141">

                    <div className="w-full h-full bg-carbon-bright/20" data-id="element-142" />
                  </div>}

                <h3 className="text-base font-bold text-carbon-deep mb-2" data-id="element-143">
                  {step.title}
                </h3>
                <p className="text-sm text-carbon-mid leading-relaxed" data-id="element-144">
                  {step.description}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}